import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {DocumentHal} from '../../models/HAL/document-hal.model';
import {Router} from '@angular/router';
import {MatIcon} from '@angular/material/icon';
import {SessionService} from '../../services/session.service';
import {Team} from '../../models/HAL/team-hal.model';
import {MatButton, MatIconButton} from '@angular/material/button';
import {NgClass} from '@angular/common';
import {DynamicConfirmationDialogComponent} from '../dynamic-confirmation-dialog/dynamic-confirmation-dialog.component';
import {DialogService} from '../../services/dialog.service';
import {SnackBarService} from '../../services/snackbar.service';
import {TranslatePipe} from '@ngx-translate/core';
import {HttpClient} from '@angular/common/http';
import {DocumentReviewDtoModel} from '../../models/DTO/DocumentReviewDto.model';
import {DocumentReviewEnum} from '../../enums/document-review.enum';
import {LoadingSpinnerService} from '../../services/loading-spinner.service';
import {MatMenuModule} from '@angular/material/menu';
import {OverflowButton} from '../../models/overflow-button.model';

@Component({
  selector: 'app-preview-document-metadata',
  imports: [
    TranslatePipe,
    MatIcon,
    MatButton,
    MatIconButton,
    NgClass,
    MatMenuModule
  ],
  templateUrl: './preview-document-metadata.component.html',
  styleUrl: './preview-document-metadata.component.scss'
})
export class PreviewDocumentMetadataComponent implements AfterViewInit, OnDestroy {

  @Input()
  documentHal: DocumentHal | undefined;

  protected hiddenButtons = new Set<string>();
  protected hiddenMenuButtons: OverflowButton[] = [];
  protected overflowButtons: OverflowButton[] = [];

  @ViewChild('breadCrumbsAndButtonsContainer') private readonly containerRef!: ElementRef<HTMLElement>;
  @ViewChild('breadcrumbEl') private readonly breadcrumbRef!: ElementRef<HTMLElement>;
  @ViewChild('primaryButton', {read: ElementRef}) private readonly primaryButtonRef!: ElementRef<HTMLElement>;
  @ViewChildren('measureButton', {read: ElementRef}) private readonly measureButtons!: QueryList<ElementRef<HTMLElement>>;

  private resizeObserver?: ResizeObserver;

  constructor(
    private readonly router: Router,
    private readonly httpClient: HttpClient,
    private readonly sessionService: SessionService,
    private readonly snackBarService: SnackBarService,
    private readonly dialogService: DialogService,
    private readonly loadingSpinnerService: LoadingSpinnerService,
    private readonly ngZone: NgZone
  ) {
    this.initOverflowButtons();
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private initOverflowButtons(): void {
    this.overflowButtons = [
      {
        key: 'rejectDocument',
        icon: 'thumb_down',
        labelKey: 'previewPdfMetadata.buttons.rejectDocument.label',
        buttonType: 'mat-button',
        action: () => this.handleRejectedDocument(),
        visible: () => !!this.documentHal?._links?.hasLink('rejectDocument'),
        disabled: () => false
      }
    ];
  }

  private setupResizeObserver(): void {
    const container = this.containerRef?.nativeElement;
    if (!container) return;
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.recalculateOverflow());
      this.resizeObserver.observe(container);
    });
    this.measureButtons.changes.subscribe(() => this.recalculateOverflow());
    setTimeout(() => this.recalculateOverflow());
  }

  private recalculateOverflow(): void {
    const container = this.containerRef?.nativeElement;
    const breadcrumb = this.breadcrumbRef?.nativeElement;
    const primaryBtn = this.primaryButtonRef?.nativeElement;
    if (!container || !breadcrumb) return;

    const containerWidth = container.clientWidth;
    const primaryBtnWidth = primaryBtn ? primaryBtn.offsetWidth + 8 : 0;
    const overflowIconWidth = 48;
    const gap = 8;

    const breadcrumbWidth = breadcrumb.scrollWidth;

    const buttonWidths = this.getButtonMeasurements();
    const visibleButtons = this.overflowButtons.filter(b => b.visible());
    const newHidden = new Set<string>();

    let usedWidth = breadcrumbWidth + primaryBtnWidth + gap;
    for (const btn of visibleButtons) {
      usedWidth += (buttonWidths.get(btn.key) ?? 0) + gap;
    }

    if (usedWidth > containerWidth) {
      usedWidth += overflowIconWidth;
      for (const btn of visibleButtons) {
        if (usedWidth <= containerWidth) break;
        const btnWidth = (buttonWidths.get(btn.key) ?? 0) + gap;
        newHidden.add(btn.key);
        usedWidth -= btnWidth;
      }
    }

    if (this.setsEqual(this.hiddenButtons, newHidden)) return;

    this.ngZone.run(() => {
      this.hiddenButtons = newHidden;
      this.hiddenMenuButtons = this.overflowButtons.filter(b => b.visible() && newHidden.has(b.key));
    });
  }

  private setsEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    for (const item of a) {
      if (!b.has(item)) return false;
    }
    return true;
  }

  private getButtonMeasurements(): Map<string, number> {
    const map = new Map<string, number>();
    if (!this.measureButtons) return map;
    this.measureButtons.forEach(ref => {
      const key = ref.nativeElement.dataset['key'];
      if (key) map.set(key, ref.nativeElement.offsetWidth);
    });
    return map;
  }

  protected isButtonHidden(key: string): boolean {
    return this.hiddenButtons.has(key);
  }

  backToCaseOverview() {
    this.loadingSpinnerService.show();
    this.router.navigate([new Team(this.sessionService.getSession().currentTeam)._links.getLink('caseOverview')?.getRoute()]).then();
  }

  backToCaseDetail() {
    this.loadingSpinnerService.show();
    this.router.navigate([new DocumentHal(this.documentHal)._links.getLink('case')?.getRoute()]).then();
  }

  protected handleApprovedDocument() {
    const dialogRef = this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'previewPdfMetadata.approveCaseModal.title',
        message: 'previewPdfMetadata.approveCaseModal.message',
        confirmButtonText: 'previewPdfMetadata.approveCaseModal.confirm',
        cancelButtonText: 'previewPdfMetadata.approveCaseModal.cancel'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        let documentReviewDtoModel: DocumentReviewDtoModel = new DocumentReviewDtoModel({
          review: DocumentReviewEnum.APPROVED
        });
        this.httpClient.patch<DocumentHal>(
          <string>this.documentHal?._links.getLink('approveDocument')!.href, documentReviewDtoModel
        ).subscribe({
          next: () => {
            this.snackBarService.open('previewPdfMetadata.snackbars.documentApproved', 3);
            this.backToCaseDetail();
          }
        });
      }
    });
  }

  protected handleRejectedDocument() {
    const dialogRef = this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'previewPdfMetadata.rejectCaseModal.title',
        message: 'previewPdfMetadata.rejectCaseModal.message',
        confirmButtonText: 'previewPdfMetadata.rejectCaseModal.confirm',
        cancelButtonText: 'previewPdfMetadata.rejectCaseModal.cancel'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        let documentReviewDtoModel: DocumentReviewDtoModel = new DocumentReviewDtoModel({
          review: DocumentReviewEnum.REJECTED
        });
        this.httpClient.patch<DocumentHal>(
          <string>this.documentHal?._links.getLink('rejectDocument')!.href, documentReviewDtoModel
        ).subscribe({
          next: () => {
            this.snackBarService.open('previewPdfMetadata.snackbars.documentRejected', 3);
            this.backToCaseDetail();
          }
        });
      }
    });
  }
}
